import { Component, ElementRef, OnInit, inject, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { AdvertViewModel } from '@models/advert/advert';
import { SupportedCultureInfo } from '@models/backend/common';
import { GoogleAnalyticsEvents } from '@models/google-analytics/google-analytics-events';
import { AdvertService } from '@services/advert.service';
import { AnalyticsService } from '@services/analytics.service';
import { BrowserWindowService } from '@services/browser-window.service';
import { CountryService } from '@services/country.service';
import { takeUntil } from 'rxjs';
import { UnSubscriptionDirective } from '@directives/unsubscribe.directive';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSelect, MatOption } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { MessageBoxComponent } from '@akelius/angular-ui-kit';
import { ClickAndKeydownDirective } from '@directives/click-and-keydown.directive';
import { TranslateModule } from '@ngx-translate/core';

enum DownloadExposeViewType {
    Language,
    Loading,
    Success,
    Error,
}

@Component({
    selector: 'download-expose',
    templateUrl: 'download-expose.component.html',
    styleUrls: ['download-expose.component.less'],
    imports: [MatIconButton, MatDialogClose, MatIcon, MatFormField, MatLabel, MatSelect, FormsModule, MatOption, LoadingIndicatorComponent, MessageBoxComponent, MatButton, ClickAndKeydownDirective, TranslateModule]
})
export class DownloadExposeComponent extends UnSubscriptionDirective implements OnInit {
    private advertService = inject(AdvertService);
    private countryService = inject(CountryService);
    private browserWindowService = inject(BrowserWindowService);
    private gaService = inject(AnalyticsService);
    dialogRef = inject<MatDialogRef<DownloadExposeComponent>>(MatDialogRef);
    args = inject<AdvertViewModel>(MAT_DIALOG_DATA);

    currentViewType: DownloadExposeViewType = DownloadExposeViewType.Language;
    selectedCultureInfo: SupportedCultureInfo;

    private readonly downloadLinkElement = viewChild<ElementRef>('downloadLink');

    get isLoading(): boolean {
        return this.currentViewType === DownloadExposeViewType.Loading;
    }

    ngOnInit(): void {
        this.selectedCultureInfo = this.countryService.getCurrentCultureInfo();
    }

    languageSelected(): void {
        this.gaService.event(GoogleAnalyticsEvents.ExposeDownloaded, 'data', `language:${this.selectedCultureInfo}`);

        this.currentViewType = DownloadExposeViewType.Loading;
        this.dialogRef.disableClose = true;
        this.advertService
            .getExpose(this.args.id, this.selectedCultureInfo)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(
                (res) => this.gotExpose(res),
                () => this.requestError(),
            );
    }

    private gotExpose(blob: Blob): void {
        this.dialogRef.disableClose = false;
        this.currentViewType = DownloadExposeViewType.Success;
        const url = this.browserWindowService.getURLConstructor().createObjectURL(blob);
        const anchor = this.downloadLinkElement().nativeElement as HTMLAnchorElement;
        anchor.href = url;
        anchor.download = `Expose_${this.args.id}.pdf`;
        anchor.click();
    }

    private requestError(): void {
        this.dialogRef.disableClose = false;
        this.currentViewType = DownloadExposeViewType.Error;
    }
}
